import type {
  BlockerKind,
  CellSpec,
  LevelSpec,
  MechanicKind,
  PieceType,
} from '../logic/types';

/**
 * The campaign is authored around five short, readable arcs.  A chapter owns
 * a palette and an environment token so the presentation layer can change the
 * room without having to infer it from individual levels.
 */
export interface ChapterMeta {
  id: number;
  title: string;
  subtitle: string;
  palette: string;
  accent: string;
  frame: string;
  environment: string;
  music: string;
  mechanics: MechanicKind[];
  levelIds: number[];
  finalLevel: number;
}

type Direction = NonNullable<CellSpec['conveyor']>;
type CellPatch = CellSpec & { r: number; c: number };

const cellKey = (r: number, c: number): string => `${r},${c}`;

const patch = (r: number, c: number, spec: CellSpec = {}): CellPatch => ({
  r,
  c,
  ...spec,
});

const holes = (...coords: [number, number][]): CellPatch[] =>
  coords.map(([r, c]) =>
    patch(r, c, {
      active: false,
      blocker: 'none',
      layers: 0,
      locked: false,
      hazard: 0,
    }),
  );

const blocker = (
  kind: Exclude<BlockerKind, 'none' | 'lock'>,
  r: number,
  c: number,
  layers = 1,
): CellPatch => patch(r, c, { blocker: kind, layers, locked: false });

const lock = (r: number, c: number): CellPatch =>
  patch(r, c, { blocker: 'lock', layers: 1, locked: true });

const portal = (group: string, r: number, c: number): CellPatch =>
  patch(r, c, { portal: group });

const portals = (
  group: string,
  ...coords: [number, number][]
): CellPatch[] => coords.map(([r, c]) => portal(group, r, c));

const conveyor = (
  direction: Direction,
  r: number,
  c: number,
): CellPatch => patch(r, c, { conveyor: direction });

const conveyors = (
  direction: Direction,
  ...coords: [number, number][]
): CellPatch[] => coords.map(([r, c]) => conveyor(direction, r, c));

const hazard = (r: number, c: number, amount = 1): CellPatch =>
  patch(r, c, { hazard: amount });

const hazards = (
  amount: number,
  ...coords: [number, number][]
): CellPatch[] => coords.map(([r, c]) => hazard(r, c, amount));

const spawns = (...coords: [number, number][]): CellPatch[] =>
  coords.map(([r, c]) => patch(r, c, { spawn: true }));

const exits = (...coords: [number, number][]): CellPatch[] =>
  coords.map(([r, c]) => patch(r, c, { exit: true }));

/**
 * Full maps are intentional.  The engine can safely use a single lookup
 * convention for every level, while the author only has to patch unusual
 * cells.  A fresh CellSpec is created for every coordinate to avoid shared
 * state when a level is restarted.
 */
const board = (
  width: number,
  height: number,
  patches: CellPatch[] = [],
): Record<string, CellSpec> => {
  const result: Record<string, CellSpec> = {};
  for (let r = 0; r < height; r += 1) {
    for (let c = 0; c < width; c += 1) {
      result[cellKey(r, c)] = {
        active: true,
        blocker: 'none',
        layers: 0,
        locked: false,
        hazard: 0,
      };
    }
  }

  for (const cell of patches) {
    if (cell.r < 0 || cell.r >= height || cell.c < 0 || cell.c >= width) {
      throw new Error(`Campaign cell ${cell.r},${cell.c} is outside ${width}x${height}`);
    }
    const { r, c, ...spec } = cell;
    result[cellKey(r, c)] = { ...result[cellKey(r, c)], ...spec };
  }
  return result;
};

/**
 * On these levels the generator must not silently use the default top-row
 * entry rule.  Every cell receives an explicit spawn flag and only the listed
 * entries are legal spawn points.
 */
const constrainedBoard = (
  width: number,
  height: number,
  allowedSpawns: [number, number][],
  patches: CellPatch[] = [],
): Record<string, CellSpec> => {
  const result = board(width, height, patches);
  for (let r = 0; r < height; r += 1) {
    for (let c = 0; c < width; c += 1) {
      result[cellKey(r, c)].spawn = false;
    }
  }
  for (const [r, c] of allowedSpawns) {
    if (!result[cellKey(r, c)] || result[cellKey(r, c)].active === false) {
      throw new Error(`Campaign spawn ${r},${c} is not an active cell`);
    }
    result[cellKey(r, c)].spawn = true;
  }
  return result;
};

const levelIds = (first: number): number[] =>
  Array.from({ length: 10 }, (_, index) => first + index);

export const CHAPTERS: ChapterMeta[] = [
  {
    id: 1,
    title: 'Dawnroot Clearing',
    subtitle: 'Wake the first lights beneath the listening leaves.',
    palette: 'sunlit-sprig',
    accent: '#ffd36a',
    frame: 'woven-amber',
    environment: 'dew-garden',
    music: 'dawnroot-pulse',
    mechanics: ['classic', 'layers', 'holes', 'locked'],
    levelIds: levelIds(1),
    finalLevel: 10,
  },
  {
    id: 2,
    title: 'Brassroot Foundry',
    subtitle: 'Teach the old garden engines to turn in rhythm.',
    palette: 'brassroot-amber',
    accent: '#f59b59',
    frame: 'clockwork-vine',
    environment: 'copper-greenhouse',
    music: 'brassroot-meter',
    mechanics: [
      'classic',
      'layers',
      'locked',
      'conveyor',
      'multiEntry',
      'spawnLimits',
      'hazard',
    ],
    levelIds: levelIds(11),
    finalLevel: 20,
  },
  {
    id: 3,
    title: 'Tideglass Grotto',
    subtitle: 'Follow the blue current where distance folds in on itself.',
    palette: 'tideglass-cyan',
    accent: '#69d9ee',
    frame: 'tidal-arch',
    environment: 'bioluminescent-cavern',
    music: 'tideglass-echo',
    mechanics: [
      'holes',
      'portals',
      'conveyor',
      'multiEntry',
      'spawnLimits',
      'layers',
      'locked',
      'hazard',
    ],
    levelIds: levelIds(21),
    finalLevel: 30,
  },
  {
    id: 4,
    title: 'Emberfall Canopy',
    subtitle: 'Hold the warm wildness at the edge of the living grove.',
    palette: 'emberfall-coral',
    accent: '#ff786b',
    frame: 'charred-petal',
    environment: 'canopy-at-dusk',
    music: 'emberfall-drift',
    mechanics: [
      'hazard',
      'layers',
      'holes',
      'conveyor',
      'multiEntry',
      'spawnLimits',
      'portals',
      'locked',
    ],
    levelIds: levelIds(31),
    finalLevel: 40,
  },
  {
    id: 5,
    title: 'Astral Loom',
    subtitle: 'Stitch every kind of light into one clear constellation.',
    palette: 'nightloom-violet',
    accent: '#c493ff',
    frame: 'orbiting-threads',
    environment: 'starfield-observatory',
    music: 'astral-loom',
    mechanics: [
      'classic',
      'holes',
      'portals',
      'conveyor',
      'locked',
      'layers',
      'hazard',
      'multiEntry',
      'spawnLimits',
    ],
    levelIds: levelIds(41),
    finalLevel: 50,
  },
];

export const LEVELS: LevelSpec[] = [
  // Chapter 1 — Dawnroot Clearing: one new idea at a time.
  {
    id: 1,
    chapter: 1,
    title: 'First Spark',
    subtitle: 'Three small lights are enough to wake the clearing.',
    width: 7,
    height: 7,
    moves: 24,
    seed: 10101,
    palette: 'sunlit-sprig',
    mechanics: ['classic'],
    objectives: [
      {
        id: 'dawn-score',
        kind: 'score',
        target: 900,
        label: 'Gather 900 glow',
        hint: 'Make clean matches and let the falling pieces do some work.',
      },
    ],
    cells: board(7, 7),
    tutorial: [
      'Drag a piece toward a neighbor, or select two touching pieces.',
      'A swap only counts when it creates a line of three or more.',
      'After a clear, watch the pieces fall before choosing your next move.',
    ],
    challenge: 'Teach the board rhythm: match, fall, refill.',
    difficulty: 1,
  },
  {
    id: 2,
    chapter: 1,
    title: 'Petal Pact',
    subtitle: 'The Cinderleaf lanterns answer when their kin gather.',
    width: 7,
    height: 7,
    moves: 25,
    seed: 10102,
    palette: 'sunlit-sprig',
    mechanics: ['classic'],
    objectives: [
      {
        id: 'cinderleaf-collection',
        kind: 'collect',
        target: 12,
        pieceType: 0,
        label: 'Collect 12 Cinderleaf lanterns',
        hint: 'The target piece is marked in the objective ribbon.',
      },
      {
        id: 'pact-score',
        kind: 'score',
        target: 1400,
        label: 'Reach 1,400 glow',
      },
    ],
    cells: board(7, 7),
    tutorial: [
      'The Cinderleaf lantern is the red star-shaped piece.',
      'Collect it by clearing it; ordinary matches still build your score.',
    ],
    challenge: 'Choose between a tempting score match and a useful collection match.',
    difficulty: 1,
  },
  {
    id: 3,
    chapter: 1,
    title: 'Glasswing Nursery',
    subtitle: 'Clear the sleeping panes without wasting the easy angles.',
    width: 7,
    height: 7,
    moves: 26,
    seed: 10103,
    palette: 'sunlit-sprig',
    mechanics: ['classic', 'layers'],
    objectives: [
      {
        id: 'nursery-glass',
        kind: 'blocker',
        target: 8,
        label: 'Crack 8 glasswing panes',
        hint: 'A match touching a pane removes one layer from it.',
      },
    ],
    cells: board(7, 7, [
      blocker('glass', 2, 2, 2),
      blocker('glass', 2, 3, 2),
      blocker('glass', 2, 4, 2),
      blocker('glass', 3, 2, 2),
      blocker('glass', 3, 4, 2),
      blocker('glass', 4, 2, 2),
      blocker('glass', 4, 3, 2),
      blocker('glass', 4, 4, 2),
    ]),
    tutorial: [
      'Layered blockers are cleared by matches beside or on their cells.',
      'Plan a match that touches several panes at once.',
    ],
    challenge: 'The central diamond rewards horizontal and vertical planning.',
    difficulty: 2,
  },
  {
    id: 4,
    chapter: 1,
    title: 'Mooncap Pantry',
    subtitle: 'Fill the pantry before the blue caps fold back into the moss.',
    width: 8,
    height: 7,
    moves: 25,
    seed: 10104,
    palette: 'sunlit-sprig',
    mechanics: ['classic'],
    objectives: [
      {
        id: 'mooncap-collection',
        kind: 'collect',
        target: 14,
        pieceType: 1,
        label: 'Collect 14 Mooncaps',
      },
      {
        id: 'pantry-score',
        kind: 'score',
        target: 1800,
        label: 'Reach 1,800 glow',
      },
    ],
    cells: board(8, 7),
    challenge: 'The wide board offers several collection routes; do not tunnel on one column.',
    difficulty: 2,
  },
  {
    id: 5,
    chapter: 1,
    title: 'Rootbound Runes',
    subtitle: 'Free the brass buds from a patient tangle of bramble.',
    width: 8,
    height: 7,
    moves: 24,
    seed: 10105,
    palette: 'sunlit-sprig',
    mechanics: ['classic', 'layers'],
    objectives: [
      {
        id: 'rootbound-bramble',
        kind: 'blocker',
        target: 12,
        label: 'Remove 12 bramble layers',
        hint: 'A bramble cell may need more than one neighboring match.',
      },
      {
        id: 'gearbud-clear',
        kind: 'clear',
        target: 10,
        pieceType: 2,
        label: 'Clear 10 Gearbuds',
      },
    ],
    cells: board(8, 7, [
      blocker('bramble', 2, 2, 2),
      blocker('bramble', 2, 3, 2),
      blocker('bramble', 2, 4, 2),
      blocker('bramble', 2, 5, 2),
      blocker('bramble', 3, 2, 1),
      blocker('bramble', 3, 5, 1),
      blocker('bramble', 4, 2, 1),
      blocker('bramble', 4, 5, 1),
      blocker('bramble', 5, 2, 2),
      blocker('bramble', 5, 3, 2),
      blocker('bramble', 5, 4, 2),
      blocker('bramble', 5, 5, 2),
    ]),
    challenge: 'Open a corridor through the ring, then harvest the Gearbuds it exposes.',
    difficulty: 2,
  },
  {
    id: 6,
    chapter: 1,
    title: 'Fourfold Flight',
    subtitle: 'A longer line leaves a filament in the air.',
    width: 8,
    height: 8,
    moves: 26,
    seed: 10106,
    palette: 'sunlit-sprig',
    mechanics: ['classic', 'layers'],
    objectives: [
      {
        id: 'fourfold-combo',
        kind: 'combo',
        target: 1,
        label: 'Activate 1 special pairing',
        hint: 'Make a four-piece line, then use its filament in a second match.',
      },
      {
        id: 'fourfold-score',
        kind: 'score',
        target: 3500,
        label: 'Reach 3,500 glow',
      },
    ],
    cells: board(8, 8, [
      blocker('glass', 1, 1, 2),
      blocker('glass', 1, 6, 2),
      blocker('glass', 6, 1, 2),
      blocker('glass', 6, 6, 2),
    ]),
    tutorial: [
      'A four-piece line creates a horizon or vertical filament.',
      'Special pieces are strongest when your next match can touch them.',
    ],
    challenge: 'The corner panes encourage a deliberate line instead of random clearing.',
    difficulty: 3,
  },
  {
    id: 7,
    chapter: 1,
    title: 'Sunken Seedbed',
    subtitle: 'Guide prism pears through the gaps to the root gates.',
    width: 8,
    height: 8,
    moves: 25,
    seed: 10107,
    palette: 'sunlit-sprig',
    mechanics: ['classic', 'holes', 'spawnLimits'],
    objectives: [
      {
        id: 'seedbed-drop',
        kind: 'drop',
        target: 3,
        pieceType: 3,
        label: 'Drop 3 Prism Pears to root gates',
        hint: 'Open a route to the marked exits at the bottom edge.',
      },
    ],
    cells: constrainedBoard(
      8,
      8,
      [
        [0, 1],
        [0, 3],
        [0, 5],
        [0, 6],
      ],
      [
        ...holes([2, 0], [2, 7], [3, 1], [3, 6], [4, 0], [4, 7]),
        ...spawns([0, 1], [0, 3], [0, 5], [0, 6]),
        ...exits([7, 1], [7, 3], [7, 5]),
      ],
    ),
    tutorial: [
      'The entry mask limits where new pieces may appear.',
      'A dropped Prism Pear must pass through an active route and leave at a root gate.',
    ],
    challenge: 'The side holes make a straight route unreliable; shape the fall with matches.',
    difficulty: 3,
  },
  {
    id: 8,
    chapter: 1,
    title: 'Lantern Lattice',
    subtitle: 'Unfasten the quiet locks before the last lantern dims.',
    width: 8,
    height: 8,
    moves: 23,
    seed: 10108,
    palette: 'sunlit-sprig',
    mechanics: ['classic', 'holes', 'locked'],
    objectives: [
      {
        id: 'lattice-locks',
        kind: 'blocker',
        target: 10,
        label: 'Release 10 lantern locks',
        hint: 'Locked cells open when a neighboring clear reaches them.',
      },
      {
        id: 'lattice-collection',
        kind: 'collect',
        target: 14,
        pieceType: 4,
        label: 'Collect 14 Coral Quills',
      },
    ],
    cells: board(8, 8, [
      ...holes([0, 0], [0, 7], [7, 0], [7, 7]),
      lock(2, 2),
      lock(2, 5),
      lock(3, 3),
      lock(3, 4),
      lock(4, 3),
      lock(4, 4),
      lock(5, 2),
      lock(5, 5),
      lock(1, 3),
      lock(6, 4),
    ]),
    challenge: 'Unlocking the center can create a cascade, but the Quills still need deliberate routing.',
    difficulty: 3,
  },
  {
    id: 9,
    chapter: 1,
    title: 'Cinderleaf Chorus',
    subtitle: 'Let the red lanterns sing in a pattern, not a panic.',
    width: 9,
    height: 7,
    moves: 23,
    seed: 10109,
    palette: 'sunlit-sprig',
    mechanics: ['classic', 'layers', 'locked'],
    objectives: [
      {
        id: 'chorus-combos',
        kind: 'combo',
        target: 2,
        label: 'Activate 2 special pairings',
        hint: 'Look for intersecting lines and save one special for a purposeful follow-up.',
      },
      {
        id: 'chorus-score',
        kind: 'score',
        target: 6500,
        label: 'Reach 6,500 glow',
      },
    ],
    cells: board(9, 7, [
      lock(1, 4),
      lock(2, 4),
      blocker('glass', 3, 1, 2),
      blocker('glass', 3, 7, 2),
      blocker('glass', 4, 1, 2),
      blocker('glass', 4, 7, 2),
      lock(5, 4),
    ]),
    challenge: 'The narrow middle makes a nova bloom or filament pairing more valuable than raw volume.',
    difficulty: 4,
  },
  {
    id: 10,
    chapter: 1,
    title: 'Groveheart Trial',
    subtitle: 'The clearing opens only for a patient, well-aimed constellation.',
    width: 9,
    height: 8,
    moves: 21,
    seed: 10110,
    palette: 'sunlit-sprig',
    mechanics: ['classic', 'layers', 'holes', 'locked'],
    objectives: [
      {
        id: 'groveheart-score',
        kind: 'score',
        target: 9000,
        label: 'Reach 9,000 glow',
      },
      {
        id: 'groveheart-blockers',
        kind: 'blocker',
        target: 18,
        label: 'Clear 18 rooted layers',
      },
      {
        id: 'groveheart-combos',
        kind: 'combo',
        target: 2,
        label: 'Activate 2 special pairings',
      },
    ],
    cells: board(9, 8, [
      ...holes([0, 0], [0, 8], [1, 1], [1, 7], [6, 1], [6, 7], [7, 0], [7, 8]),
      blocker('bramble', 2, 2, 2),
      blocker('bramble', 2, 3, 1),
      blocker('bramble', 2, 5, 1),
      blocker('bramble', 2, 6, 2),
      lock(3, 4),
      lock(4, 4),
      blocker('glass', 5, 2, 2),
      blocker('glass', 5, 6, 2),
    ]),
    challenge: 'Final clearing test: open the center, build specials, and spend the last moves on pairings.',
    difficulty: 5,
  },

  // Chapter 2 — Brassroot Foundry: movement rules become part of the puzzle.
  {
    id: 11,
    chapter: 2,
    title: 'Cogs at Dawn',
    subtitle: 'The foundry floor carries every piece one beat to the side.',
    width: 8,
    height: 8,
    moves: 25,
    seed: 20211,
    palette: 'brassroot-amber',
    mechanics: ['classic', 'conveyor'],
    objectives: [
      {
        id: 'cogs-clear',
        kind: 'clear',
        target: 18,
        pieceType: 2,
        label: 'Clear 18 Gearbuds',
      },
      {
        id: 'cogs-score',
        kind: 'score',
        target: 2600,
        label: 'Reach 2,600 glow',
      },
    ],
    cells: board(8, 8, [
      ...conveyors('right',
        [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6],
        [4, 1], [4, 2], [4, 3], [4, 4], [4, 5], [4, 6],
      ),
      ...conveyors('left',
        [2, 1], [2, 2], [2, 3], [2, 4], [2, 5], [2, 6],
        [5, 1], [5, 2], [5, 3], [5, 4], [5, 5], [5, 6],
      ),
    ]),
    tutorial: [
      'Conveyor cells move pieces after each resolution.',
      'Read the arrows before making a match; a near-match may arrive for free.',
    ],
    challenge: 'Two opposing belts turn the middle into a timing puzzle.',
    difficulty: 1,
  },
  {
    id: 12,
    chapter: 2,
    title: 'Brass Nectar',
    subtitle: 'Keep the Suncoils flowing through the warmest gears.',
    width: 8,
    height: 8,
    moves: 24,
    seed: 20212,
    palette: 'brassroot-amber',
    mechanics: ['classic', 'conveyor', 'spawnLimits'],
    objectives: [
      {
        id: 'nectar-collection',
        kind: 'collect',
        target: 18,
        pieceType: 6,
        label: 'Collect 18 Suncoils',
        hint: 'The upper entry ports are deliberately narrow.',
      },
      {
        id: 'nectar-score',
        kind: 'score',
        target: 3200,
        label: 'Reach 3,200 glow',
      },
    ],
    cells: constrainedBoard(
      8,
      8,
      [[0, 1], [0, 3], [0, 4], [0, 6]],
      [
        ...conveyors('right', [2, 1], [2, 2], [2, 3], [2, 4], [2, 5], [2, 6]),
        ...conveyors('left', [5, 1], [5, 2], [5, 3], [5, 4], [5, 5], [5, 6]),
        ...spawns([0, 1], [0, 3], [0, 4], [0, 6]),
      ],
    ),
    challenge: 'With four entry ports, a good move is one that preserves future Suncoil access.',
    difficulty: 2,
  },
  {
    id: 13,
    chapter: 2,
    title: 'Latchwork',
    subtitle: 'The brass petals are locked in a pattern that must be opened from the edge.',
    width: 8,
    height: 8,
    moves: 24,
    seed: 20213,
    palette: 'brassroot-amber',
    mechanics: ['classic', 'layers', 'locked'],
    objectives: [
      {
        id: 'latchwork-locks',
        kind: 'blocker',
        target: 12,
        label: 'Release 12 latchwork locks',
      },
      {
        id: 'latchwork-ink',
        kind: 'clear',
        target: 14,
        pieceType: 5,
        label: 'Clear 14 Inkdrops',
      },
    ],
    cells: board(8, 8, [
      ...holes([0, 0], [0, 7], [7, 0], [7, 7]),
      lock(1, 2),
      lock(1, 5),
      lock(2, 3),
      lock(2, 4),
      lock(3, 2),
      lock(3, 5),
      lock(4, 2),
      lock(4, 5),
      lock(5, 3),
      lock(5, 4),
      lock(6, 2),
      lock(6, 5),
      blocker('glass', 3, 3, 2),
      blocker('glass', 4, 4, 2),
    ]),
    challenge: 'The two inner panes slow the easiest central unlock; work the diagonal seams.',
    difficulty: 2,
  },
  {
    id: 14,
    chapter: 2,
    title: 'Twin Feeders',
    subtitle: 'Two side mouths feed one root gate, but never at the same pace.',
    width: 9,
    height: 7,
    moves: 23,
    seed: 20214,
    palette: 'brassroot-amber',
    mechanics: ['classic', 'multiEntry', 'spawnLimits', 'holes'],
    objectives: [
      {
        id: 'feeder-drop',
        kind: 'drop',
        target: 3,
        pieceType: 4,
        label: 'Drop 3 Coral Quills',
        hint: 'Feed a Quill from either side into one of the three lower gates.',
      },
    ],
    cells: constrainedBoard(
      9,
      7,
      [[1, 0], [3, 0], [5, 0], [1, 8], [3, 8], [5, 8]],
      [
        ...holes([2, 4], [3, 4], [4, 4]),
        ...spawns([1, 0], [3, 0], [5, 0], [1, 8], [3, 8], [5, 8]),
        ...exits([6, 2], [6, 4], [6, 6]),
        ...conveyors('down', [1, 2], [2, 2], [3, 2], [4, 2], [5, 2]),
        ...conveyors('down', [1, 6], [2, 6], [3, 6], [4, 6], [5, 6]),
      ],
    ),
    tutorial: [
      'A multi-entry board can spawn from either side; the glowing ports are the only entries.',
      'Use the two vertical lanes to steer a dropped piece toward the bottom gates.',
    ],
    challenge: 'Do not clear both feeder lanes at once unless the resulting fall is useful.',
    difficulty: 3,
  },
  {
    id: 15,
    chapter: 2,
    title: 'Waxworks',
    subtitle: 'Warm the amber seals layer by layer, then let the belts breathe.',
    width: 9,
    height: 8,
    moves: 24,
    seed: 20215,
    palette: 'brassroot-amber',
    mechanics: ['classic', 'layers', 'conveyor'],
    objectives: [
      {
        id: 'waxworks-layers',
        kind: 'blocker',
        target: 16,
        label: 'Melt 16 wax layers',
        hint: 'The thick seals need repeated contact from nearby clears.',
      },
      {
        id: 'waxworks-score',
        kind: 'score',
        target: 4800,
        label: 'Reach 4,800 glow',
      },
    ],
    cells: board(9, 8, [
      blocker('wax', 2, 2, 2),
      blocker('wax', 2, 3, 2),
      blocker('wax', 2, 5, 2),
      blocker('wax', 2, 6, 2),
      blocker('wax', 3, 2, 2),
      blocker('wax', 3, 6, 2),
      blocker('wax', 4, 2, 1),
      blocker('wax', 4, 6, 1),
      blocker('wax', 5, 2, 1),
      blocker('wax', 5, 3, 1),
      blocker('wax', 5, 5, 1),
      blocker('wax', 5, 6, 1),
      ...conveyors('down', [1, 1], [2, 1], [3, 1], [4, 1], [5, 1], [6, 1]),
      ...conveyors('up', [1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7]),
    ]),
    challenge: 'The two edge belts refill the sides at different heights; use them to attack both wax wings.',
    difficulty: 3,
  },
  {
    id: 16,
    chapter: 2,
    title: 'Counterflow',
    subtitle: 'The old machine pulls the upper stream against the lower stream.',
    width: 9,
    height: 8,
    moves: 22,
    seed: 20216,
    palette: 'brassroot-amber',
    mechanics: ['classic', 'holes', 'conveyor'],
    objectives: [
      {
        id: 'counterflow-drop',
        kind: 'drop',
        target: 4,
        pieceType: 3,
        label: 'Drop 4 Prism Pears',
      },
      {
        id: 'counterflow-collection',
        kind: 'collect',
        target: 12,
        pieceType: 1,
        label: 'Collect 12 Mooncaps',
      },
    ],
    cells: board(9, 8, [
      ...holes([1, 0], [1, 8], [2, 1], [2, 7], [5, 1], [5, 7], [6, 0], [6, 8]),
      ...conveyors('right', [1, 2], [1, 3], [1, 4], [1, 5], [1, 6]),
      ...conveyors('left', [6, 2], [6, 3], [6, 4], [6, 5], [6, 6]),
      ...exits([7, 2], [7, 4], [7, 6]),
      ...spawns([0, 2], [0, 4], [0, 6]),
    ]),
    challenge: 'A move that looks like progress can reverse when the next belt fires; count one turn ahead.',
    difficulty: 3,
  },
  {
    id: 17,
    chapter: 2,
    title: 'Gearseed Relay',
    subtitle: 'Pass the golden buds through a relay of one-way chambers.',
    width: 9,
    height: 8,
    moves: 23,
    seed: 20217,
    palette: 'brassroot-amber',
    mechanics: ['classic', 'conveyor', 'spawnLimits', 'layers'],
    objectives: [
      {
        id: 'gearseed-drop',
        kind: 'drop',
        target: 5,
        pieceType: 2,
        label: 'Drop 5 Gearbuds',
        hint: 'Only the three brass ports can feed the relay.',
      },
      {
        id: 'gearseed-blockers',
        kind: 'blocker',
        target: 10,
        label: 'Break 10 relay seals',
      },
    ],
    cells: constrainedBoard(
      9,
      8,
      [[0, 2], [0, 4], [0, 6]],
      [
        ...spawns([0, 2], [0, 4], [0, 6]),
        ...exits([7, 2], [7, 4], [7, 6]),
        ...conveyors('right', [2, 1], [2, 2], [2, 3], [2, 4], [2, 5], [2, 6], [2, 7]),
        ...conveyors('left', [5, 1], [5, 2], [5, 3], [5, 4], [5, 5], [5, 6], [5, 7]),
        blocker('wax', 3, 3, 2),
        blocker('wax', 3, 4, 2),
        blocker('wax', 3, 5, 2),
        blocker('glass', 4, 3, 1),
        blocker('glass', 4, 4, 1),
        blocker('glass', 4, 5, 1),
      ],
    ),
    challenge: 'Route the drops around the sealed center rather than trying to brute-force its middle.',
    difficulty: 4,
  },
  {
    id: 18,
    chapter: 2,
    title: 'Brass Bloom',
    subtitle: 'A flower of gears opens only when two rhythms overlap.',
    width: 10,
    height: 9,
    moves: 23,
    seed: 20218,
    palette: 'brassroot-amber',
    mechanics: ['classic', 'layers', 'locked', 'conveyor'],
    objectives: [
      {
        id: 'brass-bloom-combos',
        kind: 'combo',
        target: 2,
        label: 'Activate 2 special pairings',
      },
      {
        id: 'brass-bloom-blockers',
        kind: 'blocker',
        target: 12,
        label: 'Clear 12 bloom clasps',
      },
      {
        id: 'brass-bloom-score',
        kind: 'score',
        target: 9000,
        label: 'Reach 9,000 glow',
      },
    ],
    cells: board(10, 9, [
      ...conveyors('right', [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [1, 7], [1, 8]),
      ...conveyors('left', [7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6], [7, 7], [7, 8]),
      lock(2, 2),
      lock(2, 7),
      lock(6, 2),
      lock(6, 7),
      blocker('wax', 3, 3, 2),
      blocker('wax', 3, 4, 2),
      blocker('wax', 3, 5, 2),
      blocker('wax', 3, 6, 2),
      blocker('wax', 5, 3, 1),
      blocker('wax', 5, 4, 1),
      blocker('wax', 5, 5, 1),
      blocker('wax', 5, 6, 1),
    ]),
    challenge: 'Make one pairing on the upper belt and reserve the other for the central bloom.',
    difficulty: 4,
  },
  {
    id: 19,
    chapter: 2,
    title: 'Pressure Valve',
    subtitle: 'A hot seam expands whenever the foundry is left unattended.',
    width: 10,
    height: 8,
    moves: 22,
    seed: 20219,
    palette: 'brassroot-amber',
    mechanics: ['classic', 'hazard', 'conveyor'],
    objectives: [
      {
        id: 'pressure-survive',
        kind: 'survive',
        target: 14,
        label: 'Manage the pressure for 14 turns',
        hint: 'Clear heated cells before the seam reaches its next ring.',
      },
      {
        id: 'pressure-ink',
        kind: 'clear',
        target: 20,
        pieceType: 5,
        label: 'Clear 20 Inkdrops',
      },
    ],
    cells: board(10, 8, [
      ...hazards(1,
        [3, 3], [3, 4], [3, 5], [3, 6],
        [4, 3], [4, 4], [4, 5], [4, 6],
      ),
      ...hazards(2, [2, 4], [2, 5], [5, 4], [5, 5]),
      ...conveyors('right', [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [1, 7], [1, 8]),
      ...conveyors('left', [6, 1], [6, 2], [6, 3], [6, 4], [6, 5], [6, 6], [6, 7], [6, 8]),
    ]),
    tutorial: [
      'Hazard cells grow or intensify on the hazard tick; matches touching them buy room.',
      'The survival objective is about control, not simply waiting out the move counter.',
    ],
    challenge: 'The belts can carry a safe clear into the pressure seam if you time them well.',
    difficulty: 4,
  },
  {
    id: 20,
    chapter: 2,
    title: 'Foundry Heart',
    subtitle: 'Restart the ancient heart without letting its rhythm tear the garden apart.',
    width: 10,
    height: 9,
    moves: 21,
    seed: 20220,
    palette: 'brassroot-amber',
    mechanics: [
      'classic',
      'layers',
      'locked',
      'conveyor',
      'hazard',
      'multiEntry',
      'spawnLimits',
      'holes',
    ],
    objectives: [
      {
        id: 'foundry-score',
        kind: 'score',
        target: 12000,
        label: 'Reach 12,000 glow',
      },
      {
        id: 'foundry-drop',
        kind: 'drop',
        target: 4,
        pieceType: 6,
        label: 'Drop 4 Suncoils into the heart',
      },
      {
        id: 'foundry-combo',
        kind: 'combo',
        target: 2,
        label: 'Activate 2 special pairings',
      },
    ],
    cells: constrainedBoard(
      10,
      9,
      [[0, 1], [0, 3], [0, 6], [0, 8], [1, 0], [3, 0], [5, 9], [7, 9]],
      [
        ...holes([2, 0], [2, 9], [4, 1], [4, 8], [6, 0], [6, 9]),
        ...spawns([0, 1], [0, 3], [0, 6], [0, 8], [1, 0], [3, 0], [5, 9], [7, 9]),
        ...exits([8, 2], [8, 4], [8, 5], [8, 7]),
        ...conveyors('right', [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [1, 7]),
        ...conveyors('left', [7, 2], [7, 3], [7, 4], [7, 5], [7, 6], [7, 7]),
        ...hazards(1, [3, 4], [3, 5], [4, 4], [4, 5], [5, 4], [5, 5]),
        blocker('wax', 2, 3, 2),
        blocker('wax', 2, 6, 2),
        blocker('glass', 6, 3, 2),
        blocker('glass', 6, 6, 2),
        lock(3, 3),
        lock(3, 6),
      ],
    ),
    challenge: 'Final foundry test: alternate the side feeds, cool the center, and save one special for each drop lane.',
    difficulty: 5,
  },

  // Chapter 3 — Tideglass Grotto: portals turn routes into geometry.
  {
    id: 21,
    chapter: 3,
    title: 'Echo Gate',
    subtitle: 'The grotto folds a clear path through a pair of blue arches.',
    width: 8,
    height: 8,
    moves: 25,
    seed: 30321,
    palette: 'tideglass-cyan',
    mechanics: ['classic', 'portals'],
    objectives: [
      {
        id: 'echo-score',
        kind: 'score',
        target: 3200,
        label: 'Reach 3,200 glow',
      },
      {
        id: 'echo-clear',
        kind: 'clear',
        target: 12,
        pieceType: 5,
        label: 'Clear 12 Inkdrops',
      },
    ],
    cells: board(8, 8, [
      ...portals('echo-a', [1, 1], [6, 6]),
      ...portals('echo-b', [1, 6], [6, 1]),
    ]),
    tutorial: [
      'Pieces entering a portal emerge at its matching arch.',
      'A portal is a shortcut for falls and clears; it does not change adjacency for swaps.',
    ],
    challenge: 'The crossed arches make the corners effectively neighbors during cascades.',
    difficulty: 2,
  },
  {
    id: 22,
    chapter: 3,
    title: 'Blue Current',
    subtitle: 'Ride the current to gather Mooncaps from both ends of the cavern.',
    width: 8,
    height: 8,
    moves: 24,
    seed: 30322,
    palette: 'tideglass-cyan',
    mechanics: ['classic', 'portals', 'conveyor'],
    objectives: [
      {
        id: 'current-collection',
        kind: 'collect',
        target: 18,
        pieceType: 1,
        label: 'Collect 18 Mooncaps',
      },
      {
        id: 'current-clear',
        kind: 'clear',
        target: 12,
        pieceType: 3,
        label: 'Clear 12 Prism Pears',
      },
    ],
    cells: board(8, 8, [
      ...portals('current-a', [0, 2], [7, 5]),
      ...portals('current-b', [0, 5], [7, 2]),
      ...conveyors('right', [2, 1], [2, 2], [2, 3], [2, 4], [2, 5], [2, 6]),
      ...conveyors('left', [5, 1], [5, 2], [5, 3], [5, 4], [5, 5], [5, 6]),
    ]),
    challenge: 'The portals swap the ends of each belt, so a clearing route can be planned in two stages.',
    difficulty: 2,
  },
  {
    id: 23,
    chapter: 3,
    title: 'Twin Mouths',
    subtitle: 'Feed the two cave mouths and let the undertow deliver the coral.',
    width: 9,
    height: 8,
    moves: 23,
    seed: 30323,
    palette: 'tideglass-cyan',
    mechanics: ['classic', 'portals', 'multiEntry', 'spawnLimits'],
    objectives: [
      {
        id: 'mouths-drop',
        kind: 'drop',
        target: 4,
        pieceType: 4,
        label: 'Drop 4 Coral Quills',
        hint: 'Only the two upper mouths can introduce a Quill.',
      },
    ],
    cells: constrainedBoard(
      9,
      8,
      [[0, 2], [0, 6], [0, 3], [0, 5]],
      [
        ...spawns([0, 2], [0, 6], [0, 3], [0, 5]),
        ...exits([7, 2], [7, 6]),
        ...portals('mouth-left', [2, 1], [6, 5]),
        ...portals('mouth-right', [2, 7], [6, 3]),
        ...holes([3, 4], [4, 4]),
      ],
    ),
    tutorial: [
      'The two upper mouths share one board but feed different portal routes.',
      'A drop objective is complete only when the designated piece reaches an exit.',
    ],
    challenge: 'Keep both mouth lanes open; starving one side makes the other route too predictable.',
    difficulty: 3,
  },
  {
    id: 24,
    chapter: 3,
    title: 'Sunken Archive',
    subtitle: 'Break the old glass shelves without sealing the current behind them.',
    width: 9,
    height: 8,
    moves: 24,
    seed: 30324,
    palette: 'tideglass-cyan',
    mechanics: ['classic', 'portals', 'holes', 'layers'],
    objectives: [
      {
        id: 'archive-blockers',
        kind: 'blocker',
        target: 14,
        label: 'Clear 14 archive panes',
      },
      {
        id: 'archive-score',
        kind: 'score',
        target: 5200,
        label: 'Reach 5,200 glow',
      },
    ],
    cells: board(9, 8, [
      ...holes([0, 0], [0, 8], [7, 0], [7, 8], [3, 0], [4, 8]),
      ...portals('archive-a', [1, 1], [6, 7]),
      ...portals('archive-b', [1, 7], [6, 1]),
      blocker('glass', 2, 2, 2),
      blocker('glass', 2, 3, 2),
      blocker('glass', 2, 5, 2),
      blocker('glass', 2, 6, 2),
      blocker('glass', 5, 2, 1),
      blocker('glass', 5, 3, 1),
      blocker('glass', 5, 5, 1),
      blocker('glass', 5, 6, 1),
    ]),
    challenge: 'Use a portal clear to attack the far shelf; the visible shelf is not the only one in reach.',
    difficulty: 3,
  },
  {
    id: 25,
    chapter: 3,
    title: 'Driftgarden',
    subtitle: 'Spread tide-ink through the quiet pools while the garden drifts sideways.',
    width: 9,
    height: 9,
    moves: 23,
    seed: 30325,
    palette: 'tideglass-cyan',
    mechanics: ['classic', 'portals', 'conveyor', 'holes', 'hazard'],
    objectives: [
      {
        id: 'drift-spread',
        kind: 'spread',
        target: 18,
        color: 'tide-ink',
        label: 'Spread tide-ink across 18 pools',
        hint: 'Clear beside a pool to let the ink advance; do not seal its channel.',
      },
      {
        id: 'drift-collection',
        kind: 'collect',
        target: 20,
        pieceType: 4,
        label: 'Collect 20 Coral Quills',
      },
    ],
    cells: board(9, 9, [
      ...holes([0, 0], [0, 8], [8, 0], [8, 8], [2, 0], [6, 8]),
      ...portals('drift-a', [1, 1], [7, 7]),
      ...portals('drift-b', [1, 7], [7, 1]),
      ...conveyors('right', [3, 1], [3, 2], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7]),
      ...conveyors('left', [5, 1], [5, 2], [5, 3], [5, 4], [5, 5], [5, 6], [5, 7]),
      ...hazards(1, [4, 2], [4, 3], [4, 4], [4, 5], [4, 6]),
    ]),
    tutorial: [
      'Tide-ink is a board substance: nearby clears spread it into an adjacent pool.',
      'Portals and belts can carry a spread opportunity far from the first pool.',
    ],
    challenge: 'Spread from the middle outward while keeping the two drifting lanes open.',
    difficulty: 3,
  },
  {
    id: 26,
    chapter: 3,
    title: 'Prism Crossing',
    subtitle: 'A crossing of currents rewards the first true prism pairing.',
    width: 9,
    height: 9,
    moves: 23,
    seed: 30326,
    palette: 'tideglass-cyan',
    mechanics: ['classic', 'portals', 'layers'],
    objectives: [
      {
        id: 'prism-crossing-combo',
        kind: 'combo',
        target: 1,
        label: 'Activate 1 special pairing',
        hint: 'A five-piece match creates a prism seed; pair it when the crossing is occupied.',
      },
      {
        id: 'prism-crossing-clear',
        kind: 'clear',
        target: 16,
        pieceType: 3,
        label: 'Clear 16 Prism Pears',
      },
    ],
    cells: board(9, 9, [
      ...portals('prism-a', [0, 4], [8, 4]),
      ...portals('prism-b', [4, 0], [4, 8]),
      blocker('glass', 3, 3, 2),
      blocker('glass', 3, 5, 2),
      blocker('glass', 5, 3, 2),
      blocker('glass', 5, 5, 2),
      blocker('glass', 4, 4, 1),
    ]),
    challenge: 'The center is deliberately constrained so a prism seed has a meaningful target.',
    difficulty: 4,
  },
  {
    id: 27,
    chapter: 3,
    title: 'Floodgate Locks',
    subtitle: 'Open the gates in sequence so the deep current can carry its cargo.',
    width: 10,
    height: 8,
    moves: 22,
    seed: 30327,
    palette: 'tideglass-cyan',
    mechanics: ['classic', 'portals', 'locked', 'spawnLimits'],
    objectives: [
      {
        id: 'floodgate-locks',
        kind: 'blocker',
        target: 16,
        label: 'Release 16 floodgate locks',
      },
      {
        id: 'floodgate-drop',
        kind: 'drop',
        target: 3,
        pieceType: 0,
        label: 'Drop 3 Cinderleaf lanterns',
      },
    ],
    cells: constrainedBoard(
      10,
      8,
      [[0, 1], [0, 2], [0, 7], [0, 8]],
      [
        ...spawns([0, 1], [0, 2], [0, 7], [0, 8]),
        ...exits([7, 1], [7, 2], [7, 7], [7, 8]),
        ...portals('gate-left', [2, 1], [5, 8]),
        ...portals('gate-right', [2, 8], [5, 1]),
        lock(2, 3),
        lock(2, 4),
        lock(2, 5),
        lock(2, 6),
        lock(5, 3),
        lock(5, 4),
        lock(5, 5),
        lock(5, 6),
        blocker('glass', 3, 4, 2),
        blocker('glass', 3, 5, 2),
        blocker('glass', 4, 4, 1),
        blocker('glass', 4, 5, 1),
      ],
    ),
    challenge: 'The locks split the board into two banks; portals are the safe way to cross the current.',
    difficulty: 4,
  },
  {
    id: 28,
    chapter: 3,
    title: 'Deep Channel',
    subtitle: 'A long, calm channel rewards moves that prepare the next tide.',
    width: 10,
    height: 9,
    moves: 24,
    seed: 30328,
    palette: 'tideglass-cyan',
    mechanics: ['classic', 'portals', 'conveyor', 'multiEntry'],
    objectives: [
      {
        id: 'deep-channel-score',
        kind: 'score',
        target: 10500,
        label: 'Reach 10,500 glow',
      },
      {
        id: 'deep-channel-collection',
        kind: 'collect',
        target: 22,
        pieceType: 6,
        label: 'Collect 22 Suncoils',
      },
    ],
    cells: board(10, 9, [
      ...portals('channel-a', [0, 1], [8, 8]),
      ...portals('channel-b', [0, 8], [8, 1]),
      ...conveyors('right', [3, 1], [3, 2], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7], [3, 8]),
      ...conveyors('left', [5, 1], [5, 2], [5, 3], [5, 4], [5, 5], [5, 6], [5, 7], [5, 8]),
      ...spawns([0, 2], [0, 4], [0, 5], [0, 7]),
    ]),
    challenge: 'This is a planning level rather than a blocker race: build a cascade before the next current shift.',
    difficulty: 4,
  },
  {
    id: 29,
    chapter: 3,
    title: 'Mirror Maze',
    subtitle: 'The far side is closer than it looks, but only through the right arch.',
    width: 10,
    height: 9,
    moves: 22,
    seed: 30329,
    palette: 'tideglass-cyan',
    mechanics: ['classic', 'holes', 'portals', 'locked', 'layers'],
    objectives: [
      {
        id: 'mirror-blockers',
        kind: 'blocker',
        target: 18,
        label: 'Clear 18 mirrored seals',
      },
      {
        id: 'mirror-combos',
        kind: 'combo',
        target: 2,
        label: 'Activate 2 special pairings',
      },
    ],
    cells: board(10, 9, [
      ...holes([0, 0], [0, 9], [1, 1], [1, 8], [7, 1], [7, 8], [8, 0], [8, 9]),
      ...portals('mirror-a', [2, 1], [6, 8]),
      ...portals('mirror-b', [2, 8], [6, 1]),
      lock(2, 3),
      lock(2, 6),
      lock(3, 3),
      lock(3, 6),
      lock(4, 3),
      lock(4, 6),
      blocker('glass', 3, 4, 2),
      blocker('glass', 3, 5, 2),
      blocker('glass', 4, 4, 2),
      blocker('glass', 4, 5, 2),
    ]),
    challenge: 'Pairing in the near half can clear the far seals through a portal, but saves fewer moves than a local line.',
    difficulty: 4,
  },
  {
    id: 30,
    chapter: 3,
    title: "Leviathan's Eye",
    subtitle: 'Center the living current and the grotto will show the next sky.',
    width: 10,
    height: 10,
    moves: 21,
    seed: 30330,
    palette: 'tideglass-cyan',
    mechanics: [
      'classic',
      'holes',
      'portals',
      'conveyor',
      'layers',
      'locked',
      'multiEntry',
      'spawnLimits',
      'hazard',
    ],
    objectives: [
      {
        id: 'leviathan-drop',
        kind: 'drop',
        target: 5,
        pieceType: 3,
        label: 'Drop 5 Prism Pears into the eye',
      },
      {
        id: 'leviathan-combo',
        kind: 'combo',
        target: 2,
        label: 'Activate 2 special pairings',
      },
      {
        id: 'leviathan-score',
        kind: 'score',
        target: 15000,
        label: 'Reach 15,000 glow',
      },
    ],
    cells: constrainedBoard(
      10,
      10,
      [[0, 1], [0, 3], [0, 6], [0, 8], [1, 0], [8, 9]],
      [
        ...holes([2, 0], [2, 9], [3, 1], [3, 8], [6, 1], [6, 8], [7, 0], [7, 9]),
        ...spawns([0, 1], [0, 3], [0, 6], [0, 8], [1, 0], [8, 9]),
        ...exits([9, 2], [9, 4], [9, 5], [9, 7]),
        ...portals('eye-a', [1, 2], [8, 7]),
        ...portals('eye-b', [1, 7], [8, 2]),
        ...conveyors('right', [3, 2], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7]),
        ...conveyors('left', [6, 2], [6, 3], [6, 4], [6, 5], [6, 6], [6, 7]),
        ...hazards(1, [4, 3], [4, 4], [4, 5], [4, 6], [5, 3], [5, 4], [5, 5], [5, 6]),
        blocker('wax', 2, 3, 2),
        blocker('wax', 2, 6, 2),
        blocker('glass', 7, 3, 2),
        blocker('glass', 7, 6, 2),
        lock(4, 2),
        lock(4, 7),
      ],
    ),
    challenge: 'Final grotto test: every route is useful, but no route is safe to open twice in a row.',
    difficulty: 5,
  },

  // Chapter 4 — Emberfall Canopy: hazards and spread create pressure over time.
  {
    id: 31,
    chapter: 4,
    title: 'First Ember',
    subtitle: 'Keep the small warm seam from climbing into the canopy.',
    width: 8,
    height: 8,
    moves: 24,
    seed: 40431,
    palette: 'emberfall-coral',
    mechanics: ['classic', 'hazard'],
    objectives: [
      {
        id: 'ember-survive',
        kind: 'survive',
        target: 10,
        label: 'Manage the emberline for 10 turns',
        hint: 'Touching an ember cell with a clear slows its next flare.',
      },
      {
        id: 'ember-clear',
        kind: 'clear',
        target: 14,
        pieceType: 0,
        label: 'Clear 14 Cinderleaf lanterns',
      },
    ],
    cells: board(8, 8, [
      ...hazards(1, [3, 3], [3, 4], [4, 3], [4, 4]),
      ...hazards(2, [2, 3], [2, 4], [5, 3], [5, 4]),
    ]),
    tutorial: [
      'Ember cells flare after turns and can spread to nearby active cells.',
      'A clear touching the seam keeps the hazard manageable while advancing your other objective.',
    ],
    challenge: 'Learn the cadence before the seam fills the center.',
    difficulty: 2,
  },
  {
    id: 32,
    chapter: 4,
    title: 'Thorn in the Glow',
    subtitle: 'The canopy’s old bramble burns slowly, but it burns in layers.',
    width: 8,
    height: 8,
    moves: 23,
    seed: 40432,
    palette: 'emberfall-coral',
    mechanics: ['classic', 'hazard', 'layers'],
    objectives: [
      {
        id: 'thorn-bramble',
        kind: 'blocker',
        target: 14,
        label: 'Remove 14 ember-bramble layers',
      },
      {
        id: 'thorn-mooncap',
        kind: 'clear',
        target: 12,
        pieceType: 1,
        label: 'Clear 12 Mooncaps',
      },
    ],
    cells: board(8, 8, [
      ...hazards(1, [1, 3], [1, 4], [2, 3], [2, 4], [5, 3], [5, 4], [6, 3], [6, 4]),
      blocker('bramble', 3, 2, 2),
      blocker('bramble', 3, 3, 2),
      blocker('bramble', 3, 4, 2),
      blocker('bramble', 3, 5, 2),
      blocker('bramble', 4, 2, 1),
      blocker('bramble', 4, 3, 1),
      blocker('bramble', 4, 4, 1),
      blocker('bramble', 4, 5, 1),
    ]),
    challenge: 'Attack the bramble from both sides so the two hazard banks do not meet behind it.',
    difficulty: 2,
  },
  {
    id: 33,
    chapter: 4,
    title: 'Ashwind Conveyor',
    subtitle: 'A warm crosswind carries sparks through the branches in alternating rows.',
    width: 9,
    height: 8,
    moves: 22,
    seed: 40433,
    palette: 'emberfall-coral',
    mechanics: ['classic', 'hazard', 'conveyor'],
    objectives: [
      {
        id: 'ashwind-score',
        kind: 'score',
        target: 7500,
        label: 'Reach 7,500 glow',
      },
      {
        id: 'ashwind-survive',
        kind: 'survive',
        target: 12,
        label: 'Hold the canopy for 12 turns',
      },
    ],
    cells: board(9, 8, [
      ...conveyors('right', [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [1, 7]),
      ...conveyors('left', [2, 1], [2, 2], [2, 3], [2, 4], [2, 5], [2, 6], [2, 7]),
      ...conveyors('right', [5, 1], [5, 2], [5, 3], [5, 4], [5, 5], [5, 6], [5, 7]),
      ...conveyors('left', [6, 1], [6, 2], [6, 3], [6, 4], [6, 5], [6, 6], [6, 7]),
      ...hazards(1, [3, 3], [3, 4], [3, 5], [4, 3], [4, 4], [4, 5]),
    ]),
    challenge: 'Score comes from riding the alternating belts into long clears before the ashwind advances.',
    difficulty: 3,
  },
  {
    id: 34,
    chapter: 4,
    title: 'Rootfire Spread',
    subtitle: 'Guide ember-sap through the roots before it finds the canopy’s heart.',
    width: 9,
    height: 9,
    moves: 23,
    seed: 40434,
    palette: 'emberfall-coral',
    mechanics: ['classic', 'hazard', 'holes', 'layers'],
    objectives: [
      {
        id: 'rootfire-spread',
        kind: 'spread',
        target: 18,
        color: 'ember-sap',
        label: 'Spread ember-sap across 18 roots',
        hint: 'Clear beside a lit root to spread the sap, then cool it before it flares.',
      },
      {
        id: 'rootfire-blockers',
        kind: 'blocker',
        target: 10,
        label: 'Clear 10 charred bramble layers',
      },
    ],
    cells: board(9, 9, [
      ...holes([0, 0], [0, 8], [1, 1], [1, 7], [7, 1], [7, 7], [8, 0], [8, 8]),
      ...hazards(1, [3, 3], [3, 4], [3, 5], [4, 3], [4, 4], [4, 5], [5, 3], [5, 4], [5, 5]),
      blocker('bramble', 2, 3, 2),
      blocker('bramble', 2, 4, 2),
      blocker('bramble', 2, 5, 2),
      blocker('bramble', 6, 3, 1),
      blocker('bramble', 6, 4, 1),
    ]),
    tutorial: [
      'Spread objectives reward touching a substance, not clearing it away immediately.',
      'Once enough roots are lit, cool the center so the hazard cannot overtake the route.',
    ],
    challenge: 'Choose the right time to let the sap expand; early greedy clears can cut off the lower roots.',
    difficulty: 3,
  },
  {
    id: 35,
    chapter: 4,
    title: 'Falling Cores',
    subtitle: 'Pull bright cores out of the canopy before the fire closes the lanes.',
    width: 10,
    height: 8,
    moves: 22,
    seed: 40435,
    palette: 'emberfall-coral',
    mechanics: ['classic', 'hazard', 'multiEntry', 'spawnLimits'],
    objectives: [
      {
        id: 'falling-cores-drop',
        kind: 'drop',
        target: 4,
        pieceType: 3,
        label: 'Drop 4 Prism Pears through the firebreak',
      },
      {
        id: 'falling-cores-clear',
        kind: 'clear',
        target: 16,
        pieceType: 6,
        label: 'Clear 16 Suncoils',
      },
    ],
    cells: constrainedBoard(
      10,
      8,
      [[0, 1], [0, 4], [0, 7], [1, 0], [3, 0], [5, 9], [7, 9]],
      [
        ...spawns([0, 1], [0, 4], [0, 7], [1, 0], [3, 0], [5, 9], [7, 9]),
        ...exits([7, 2], [7, 4], [7, 6]),
        ...hazards(1, [2, 2], [2, 3], [2, 4], [2, 5], [2, 6], [2, 7]),
        ...hazards(2, [4, 3], [4, 4], [4, 5], [4, 6]),
        ...holes([3, 2], [3, 7]),
      ],
    ),
    challenge: 'The side feeds are emergency entries; use them to save a stalled drop lane.',
    difficulty: 4,
  },
  {
    id: 36,
    chapter: 4,
    title: 'Combustion Study',
    subtitle: 'A controlled flare is the safest way to wake the sleeping specials.',
    width: 9,
    height: 9,
    moves: 22,
    seed: 40436,
    palette: 'emberfall-coral',
    mechanics: ['classic', 'hazard', 'layers'],
    objectives: [
      {
        id: 'combustion-combo',
        kind: 'combo',
        target: 1,
        label: 'Activate 1 special pairing',
        hint: 'Build a special beside the warm core, then combine it before the flare grows.',
      },
      {
        id: 'combustion-score',
        kind: 'score',
        target: 9000,
        label: 'Reach 9,000 glow',
      },
    ],
    cells: board(9, 9, [
      ...hazards(1, [3, 3], [3, 4], [3, 5], [4, 3], [4, 5], [5, 3], [5, 4], [5, 5]),
      hazard(4, 4, 2),
      blocker('wax', 2, 2, 2),
      blocker('wax', 2, 6, 2),
      blocker('wax', 6, 2, 2),
      blocker('wax', 6, 6, 2),
      blocker('glass', 2, 4, 1),
      blocker('glass', 6, 4, 1),
    ]),
    challenge: 'Use the central heat as an activation window, not as a reason to rush every move.',
    difficulty: 4,
  },
  {
    id: 37,
    chapter: 4,
    title: 'Wildfire Ring',
    subtitle: 'The ring expands one ember at a time; break it at its weakest seam.',
    width: 10,
    height: 8,
    moves: 21,
    seed: 40437,
    palette: 'emberfall-coral',
    mechanics: ['classic', 'hazard', 'holes', 'layers'],
    objectives: [
      {
        id: 'wildfire-survive',
        kind: 'survive',
        target: 16,
        label: 'Hold the ring for 16 turns',
      },
      {
        id: 'wildfire-spread',
        kind: 'spread',
        target: 24,
        color: 'ember-sap',
        label: 'Spread ember-sap across 24 roots',
      },
    ],
    cells: board(10, 8, [
      ...holes([0, 0], [0, 9], [7, 0], [7, 9], [1, 1], [1, 8], [6, 1], [6, 8]),
      ...hazards(1,
        [2, 2], [2, 3], [2, 4], [2, 5], [2, 6], [2, 7],
        [5, 2], [5, 3], [5, 4], [5, 5], [5, 6], [5, 7],
      ),
      ...hazards(2, [3, 2], [4, 2], [3, 7], [4, 7]),
      blocker('bramble', 3, 3, 2),
      blocker('bramble', 3, 6, 2),
      blocker('bramble', 4, 3, 1),
      blocker('bramble', 4, 6, 1),
    ]),
    challenge: 'Do not over-clear one side: the ring is easier to contain when its two weak seams are opened together.',
    difficulty: 4,
  },
  {
    id: 38,
    chapter: 4,
    title: 'Cinder Lock',
    subtitle: 'Open the charred vault while the belts carry heat toward the hinges.',
    width: 10,
    height: 9,
    moves: 21,
    seed: 40438,
    palette: 'emberfall-coral',
    mechanics: ['classic', 'hazard', 'locked', 'layers', 'conveyor'],
    objectives: [
      {
        id: 'cinder-lock-blockers',
        kind: 'blocker',
        target: 20,
        label: 'Clear 20 cinder-lock layers',
      },
      {
        id: 'cinder-lock-drop',
        kind: 'drop',
        target: 3,
        pieceType: 5,
        label: 'Drop 3 Inkdrops into the vault',
      },
    ],
    cells: board(10, 9, [
      ...hazards(1, [3, 3], [3, 4], [3, 5], [3, 6], [4, 3], [4, 6], [5, 3], [5, 4], [5, 5], [5, 6]),
      ...conveyors('right', [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [1, 7], [1, 8]),
      ...conveyors('left', [7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6], [7, 7], [7, 8]),
      ...exits([8, 3], [8, 4], [8, 5], [8, 6]),
      lock(2, 2),
      lock(2, 3),
      lock(2, 6),
      lock(2, 7),
      lock(6, 2),
      lock(6, 3),
      lock(6, 6),
      lock(6, 7),
      blocker('bramble', 3, 2, 2),
      blocker('bramble', 3, 7, 2),
      blocker('bramble', 5, 2, 1),
      blocker('bramble', 5, 7, 1),
    ]),
    challenge: 'The hot center is a wall until the belts open its flanking locks.',
    difficulty: 4,
  },
  {
    id: 39,
    chapter: 4,
    title: 'Heat Sink',
    subtitle: 'Send the excess fire through the hidden arches and keep the sink clear.',
    width: 10,
    height: 9,
    moves: 22,
    seed: 40439,
    palette: 'emberfall-coral',
    mechanics: ['classic', 'hazard', 'holes', 'portals', 'multiEntry'],
    objectives: [
      {
        id: 'heat-sink-combos',
        kind: 'combo',
        target: 2,
        label: 'Activate 2 special pairings',
      },
      {
        id: 'heat-sink-score',
        kind: 'score',
        target: 12000,
        label: 'Reach 12,000 glow',
      },
      {
        id: 'heat-sink-clear',
        kind: 'clear',
        target: 18,
        pieceType: 2,
        label: 'Clear 18 Gearbuds',
      },
    ],
    cells: board(10, 9, [
      ...holes([0, 0], [0, 9], [8, 0], [8, 9], [2, 0], [6, 9]),
      ...portals('sink-a', [1, 1], [7, 8]),
      ...portals('sink-b', [1, 8], [7, 1]),
      ...hazards(1, [3, 3], [3, 4], [3, 5], [3, 6], [4, 3], [4, 4], [4, 5], [4, 6], [5, 3], [5, 4], [5, 5], [5, 6]),
      ...hazards(2, [4, 4], [4, 5]),
      ...spawns([0, 2], [0, 4], [0, 5], [0, 7]),
    ]),
    challenge: 'The two arch pairs let a special pairing hit both heat banks if prepared from the right side.',
    difficulty: 4,
  },
  {
    id: 40,
    chapter: 4,
    title: 'Emberfall Crown',
    subtitle: 'Crown the canopy with a controlled blaze and leave the roots alive.',
    width: 10,
    height: 10,
    moves: 23,
    seed: 40440,
    palette: 'emberfall-coral',
    mechanics: [
      'classic',
      'hazard',
      'layers',
      'holes',
      'conveyor',
      'portals',
      'locked',
      'multiEntry',
      'spawnLimits',
    ],
    objectives: [
      {
        id: 'ember-crown-score',
        kind: 'score',
        target: 18000,
        label: 'Reach 18,000 glow',
      },
      {
        id: 'ember-crown-spread',
        kind: 'spread',
        target: 30,
        color: 'ember-sap',
        label: 'Spread ember-sap across 30 roots',
      },
      {
        id: 'ember-crown-combo',
        kind: 'combo',
        target: 3,
        label: 'Activate 3 special pairings',
      },
    ],
    cells: constrainedBoard(
      10,
      10,
      [[0, 1], [0, 3], [0, 6], [0, 8], [1, 0], [8, 9]],
      [
        ...holes([2, 0], [2, 9], [3, 1], [3, 8], [6, 1], [6, 8], [7, 0], [7, 9]),
        ...spawns([0, 1], [0, 3], [0, 6], [0, 8], [1, 0], [8, 9]),
        ...exits([9, 2], [9, 4], [9, 5], [9, 7]),
        ...portals('crown-a', [1, 2], [8, 7]),
        ...portals('crown-b', [1, 7], [8, 2]),
        ...conveyors('right', [3, 2], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7]),
        ...conveyors('left', [6, 2], [6, 3], [6, 4], [6, 5], [6, 6], [6, 7]),
        ...hazards(1, [4, 3], [4, 4], [4, 5], [4, 6], [5, 3], [5, 4], [5, 5], [5, 6]),
        blocker('bramble', 2, 3, 2),
        blocker('bramble', 2, 6, 2),
        blocker('wax', 7, 3, 2),
        blocker('wax', 7, 6, 2),
        lock(4, 2),
        lock(4, 7),
        lock(5, 2),
        lock(5, 7),
      ],
    ),
    challenge: 'Final canopy test: spread enough to score the crown, then spend the remaining space on deliberate pairings.',
    difficulty: 5,
  },

  // Chapter 5 — Astral Loom: a synthesis arc, with each level asking for a different kind of control.
  {
    id: 41,
    chapter: 5,
    title: 'Loomwake',
    subtitle: 'The first star-thread responds to a lock opened at the right moment.',
    width: 8,
    height: 8,
    moves: 24,
    seed: 50541,
    palette: 'nightloom-violet',
    mechanics: ['classic', 'portals', 'locked'],
    objectives: [
      {
        id: 'loomwake-locks',
        kind: 'blocker',
        target: 10,
        label: 'Release 10 star locks',
      },
      {
        id: 'loomwake-combo',
        kind: 'combo',
        target: 1,
        label: 'Activate 1 special pairing',
        hint: 'Open the center before using the first pairing so its effect can travel.',
      },
    ],
    cells: board(8, 8, [
      ...portals('loomwake-a', [1, 1], [6, 6]),
      ...portals('loomwake-b', [1, 6], [6, 1]),
      lock(2, 2),
      lock(2, 5),
      lock(3, 3),
      lock(3, 4),
      lock(4, 3),
      lock(4, 4),
      lock(5, 2),
      lock(5, 5),
      lock(1, 3),
      lock(6, 4),
    ]),
    tutorial: [
      'This chapter combines familiar rules: portal routes, locks, and intentional specials.',
      'The objective ribbon always names the effect worth setting up next.',
    ],
    challenge: 'The same geometry as an earlier lock level now has portal shortcuts; use the new route, not the old habit.',
    difficulty: 3,
  },
  {
    id: 42,
    chapter: 5,
    title: 'Sevenfold Orbit',
    subtitle: 'Seven lights circle the loom, but only a few can enter each orbit.',
    width: 9,
    height: 8,
    moves: 23,
    seed: 50542,
    palette: 'nightloom-violet',
    mechanics: ['classic', 'holes', 'portals', 'conveyor'],
    objectives: [
      {
        id: 'orbit-collection',
        kind: 'collect',
        target: 24,
        pieceType: 6,
        label: 'Collect 24 Suncoils',
      },
      {
        id: 'orbit-drop',
        kind: 'drop',
        target: 2,
        pieceType: 3,
        label: 'Drop 2 Prism Pears through orbit gates',
      },
    ],
    cells: board(9, 8, [
      ...holes([0, 0], [0, 8], [7, 0], [7, 8], [2, 0], [5, 8]),
      ...portals('orbit-a', [1, 1], [6, 7]),
      ...portals('orbit-b', [1, 7], [6, 1]),
      ...conveyors('right', [3, 1], [3, 2], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7]),
      ...conveyors('left', [4, 1], [4, 2], [4, 3], [4, 4], [4, 5], [4, 6], [4, 7]),
      ...spawns([0, 2], [0, 4], [0, 5], [0, 6]),
      ...exits([7, 2], [7, 4], [7, 6]),
    ]),
    challenge: 'A drop can ride a belt into a portal and emerge above a different exit lane.',
    difficulty: 3,
  },
  {
    id: 43,
    chapter: 5,
    title: 'Vacuum Garden',
    subtitle: 'The quiet between stars pulls at every unguarded light.',
    width: 9,
    height: 9,
    moves: 22,
    seed: 50543,
    palette: 'nightloom-violet',
    mechanics: ['classic', 'hazard', 'multiEntry', 'spawnLimits'],
    objectives: [
      {
        id: 'vacuum-score',
        kind: 'score',
        target: 14000,
        label: 'Reach 14,000 glow',
      },
      {
        id: 'vacuum-survive',
        kind: 'survive',
        target: 12,
        label: 'Hold the vacuum garden for 12 turns',
      },
    ],
    cells: constrainedBoard(
      9,
      9,
      [[0, 1], [0, 4], [0, 7], [1, 0], [4, 0], [7, 8]],
      [
        ...spawns([0, 1], [0, 4], [0, 7], [1, 0], [4, 0], [7, 8]),
        ...hazards(1, [3, 3], [3, 4], [3, 5], [4, 3], [4, 4], [4, 5], [5, 3], [5, 4], [5, 5]),
        ...hazards(2, [4, 4]),
        ...holes([2, 2], [2, 6], [6, 2], [6, 6]),
      ],
    ),
    challenge: 'The sparse entries make every refill meaningful; clear near the vacuum before it closes the center.',
    difficulty: 4,
  },
  {
    id: 44,
    chapter: 5,
    title: 'Lattice of Seven',
    subtitle: 'Every familiar piece has a place in this seven-point lock.',
    width: 10,
    height: 9,
    moves: 22,
    seed: 50544,
    palette: 'nightloom-violet',
    mechanics: ['classic', 'holes', 'layers', 'locked'],
    objectives: [
      {
        id: 'seven-lattice-blockers',
        kind: 'blocker',
        target: 22,
        label: 'Clear 22 lattice layers',
      },
      {
        id: 'seven-lattice-clear',
        kind: 'clear',
        target: 18,
        pieceType: 4,
        label: 'Clear 18 Coral Quills',
      },
    ],
    cells: board(10, 9, [
      ...holes([0, 0], [0, 9], [8, 0], [8, 9], [1, 1], [1, 8], [7, 1], [7, 8]),
      lock(2, 2),
      lock(2, 7),
      lock(3, 3),
      lock(3, 6),
      lock(4, 4),
      lock(4, 5),
      lock(5, 3),
      lock(5, 6),
      lock(6, 2),
      lock(6, 7),
      blocker('meteor', 2, 4, 2),
      blocker('meteor', 2, 5, 2),
      blocker('meteor', 6, 4, 1),
      blocker('meteor', 6, 5, 1),
    ]),
    challenge: 'The meteor layers are slow anchors; unlock around them and let the outer ring cascade inward.',
    difficulty: 4,
  },
  {
    id: 45,
    chapter: 5,
    title: 'Comet Relay',
    subtitle: 'Send comet fragments through a moving relay before they burn out.',
    width: 10,
    height: 9,
    moves: 21,
    seed: 50545,
    palette: 'nightloom-violet',
    mechanics: ['classic', 'conveyor', 'portals', 'hazard', 'spawnLimits'],
    objectives: [
      {
        id: 'comet-relay-drop',
        kind: 'drop',
        target: 5,
        pieceType: 0,
        label: 'Drop 5 Cinderleaf lanterns',
      },
      {
        id: 'comet-relay-spread',
        kind: 'spread',
        target: 20,
        color: 'star-dust',
        label: 'Spread star-dust across 20 lanes',
      },
    ],
    cells: constrainedBoard(
      10,
      9,
      [[0, 1], [0, 4], [0, 7]],
      [
        ...spawns([0, 1], [0, 4], [0, 7]),
        ...exits([8, 2], [8, 4], [8, 7]),
        ...portals('comet-a', [2, 1], [6, 8]),
        ...portals('comet-b', [2, 8], [6, 1]),
        ...conveyors('right', [3, 2], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7]),
        ...conveyors('left', [5, 2], [5, 3], [5, 4], [5, 5], [5, 6], [5, 7]),
        ...hazards(1, [4, 3], [4, 4], [4, 5], [4, 6]),
      ],
    ),
    challenge: 'The safe comet route alternates portal and belt; taking the direct-looking route feeds the hazard.',
    difficulty: 4,
  },
  {
    id: 46,
    chapter: 5,
    title: 'Polarity Bloom',
    subtitle: 'Two opposite blooms can become a single, board-wide pulse.',
    width: 10,
    height: 10,
    moves: 22,
    seed: 50546,
    palette: 'nightloom-violet',
    mechanics: ['classic', 'portals', 'conveyor', 'layers'],
    objectives: [
      {
        id: 'polarity-combo',
        kind: 'combo',
        target: 2,
        label: 'Activate 2 special pairings',
        hint: 'Build opposite-direction filaments and pair them across the central seam.',
      },
      {
        id: 'polarity-collection',
        kind: 'collect',
        target: 24,
        pieceType: 5,
        label: 'Collect 24 Inkdrops',
      },
    ],
    cells: board(10, 10, [
      ...portals('polarity-a', [0, 2], [9, 7]),
      ...portals('polarity-b', [0, 7], [9, 2]),
      ...conveyors('right', [2, 1], [2, 2], [2, 3], [2, 4], [2, 5], [2, 6], [2, 7], [2, 8]),
      ...conveyors('left', [7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6], [7, 7], [7, 8]),
      blocker('glass', 3, 3, 2),
      blocker('glass', 3, 4, 2),
      blocker('glass', 3, 5, 2),
      blocker('glass', 3, 6, 2),
      blocker('glass', 6, 3, 1),
      blocker('glass', 6, 4, 1),
      blocker('glass', 6, 5, 1),
      blocker('glass', 6, 6, 1),
    ]),
    challenge: 'A pairing in one belt should prepare the other; firing both locally wastes the polarity window.',
    difficulty: 4,
  },
  {
    id: 47,
    chapter: 5,
    title: 'Shifting Vault',
    subtitle: 'Meteor seeds drift into the vault and must be opened before they harden.',
    width: 10,
    height: 10,
    moves: 21,
    seed: 50547,
    palette: 'nightloom-violet',
    mechanics: ['classic', 'holes', 'locked', 'layers', 'conveyor'],
    objectives: [
      {
        id: 'shifting-vault-blockers',
        kind: 'blocker',
        target: 24,
        label: 'Clear 24 shifting vault layers',
      },
      {
        id: 'shifting-vault-score',
        kind: 'score',
        target: 15000,
        label: 'Reach 15,000 glow',
      },
    ],
    cells: board(10, 10, [
      ...holes([0, 0], [0, 9], [1, 1], [1, 8], [8, 1], [8, 8], [9, 0], [9, 9]),
      ...conveyors('down', [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3]),
      ...conveyors('up', [1, 6], [2, 6], [3, 6], [4, 6], [5, 6], [6, 6], [7, 6]),
      lock(2, 2),
      lock(2, 7),
      lock(7, 2),
      lock(7, 7),
      blocker('meteor', 3, 3, 3),
      blocker('meteor', 3, 6, 3),
      blocker('meteor', 4, 3, 2),
      blocker('meteor', 4, 6, 2),
      blocker('meteor', 5, 3, 2),
      blocker('meteor', 5, 6, 2),
    ]),
    challenge: 'The two belts shift the vault’s pressure from one side to the other; clear the active side first.',
    difficulty: 4,
  },
  {
    id: 48,
    chapter: 5,
    title: 'Thread the Needle',
    subtitle: 'A narrow constellation is all that remains between the loom and silence.',
    width: 11,
    height: 9,
    moves: 20,
    seed: 50548,
    palette: 'nightloom-violet',
    mechanics: ['classic', 'multiEntry', 'spawnLimits', 'portals', 'holes'],
    objectives: [
      {
        id: 'needle-collection',
        kind: 'collect',
        target: 28,
        pieceType: 0,
        label: 'Collect 28 Cinderleaf lanterns',
      },
      {
        id: 'needle-drop',
        kind: 'drop',
        target: 4,
        pieceType: 6,
        label: 'Drop 4 Suncoils through the needle',
      },
    ],
    cells: constrainedBoard(
      11,
      9,
      [[0, 1], [0, 5], [0, 9], [1, 0], [4, 0], [7, 10]],
      [
        ...holes([0, 0], [0, 10], [2, 2], [2, 8], [6, 2], [6, 8], [8, 0], [8, 10]),
        ...spawns([0, 1], [0, 5], [0, 9], [1, 0], [4, 0], [7, 10]),
        ...exits([8, 3], [8, 5], [8, 7]),
        ...portals('needle-a', [1, 2], [7, 8]),
        ...portals('needle-b', [1, 8], [7, 2]),
      ],
    ),
    challenge: 'There are many entry points but only three useful exits; preserve the middle thread.',
    difficulty: 4,
  },
  {
    id: 49,
    chapter: 5,
    title: 'Event Horizon',
    subtitle: 'The loom is almost whole; every special pulse now changes the horizon.',
    width: 11,
    height: 10,
    moves: 21,
    seed: 50549,
    palette: 'nightloom-violet',
    mechanics: [
      'classic',
      'holes',
      'portals',
      'conveyor',
      'locked',
      'layers',
      'hazard',
      'multiEntry',
      'spawnLimits',
    ],
    objectives: [
      {
        id: 'horizon-combos',
        kind: 'combo',
        target: 3,
        label: 'Activate 3 special pairings',
      },
      {
        id: 'horizon-blockers',
        kind: 'blocker',
        target: 28,
        label: 'Clear 28 horizon layers',
      },
      {
        id: 'horizon-survive',
        kind: 'survive',
        target: 18,
        label: 'Stabilize the horizon for 18 turns',
      },
    ],
    cells: constrainedBoard(
      11,
      10,
      [[0, 1], [0, 4], [0, 6], [0, 9], [1, 0], [4, 0], [8, 10], [9, 8]],
      [
        ...holes([2, 0], [2, 10], [3, 2], [3, 8], [6, 2], [6, 8], [7, 0], [7, 10]),
        ...spawns([0, 1], [0, 4], [0, 6], [0, 9], [1, 0], [4, 0], [8, 10], [9, 8]),
        ...exits([9, 3], [9, 5], [9, 7]),
        ...portals('horizon-a', [1, 2], [8, 8]),
        ...portals('horizon-b', [1, 8], [8, 2]),
        ...conveyors('right', [3, 3], [3, 4], [3, 5], [3, 6], [3, 7]),
        ...conveyors('left', [6, 3], [6, 4], [6, 5], [6, 6], [6, 7]),
        ...hazards(1, [4, 4], [4, 5], [4, 6], [5, 4], [5, 5], [5, 6]),
        ...hazards(2, [5, 5]),
        lock(2, 3),
        lock(2, 7),
        lock(7, 3),
        lock(7, 7),
        blocker('meteor', 3, 4, 2),
        blocker('meteor', 3, 5, 2),
        blocker('meteor', 3, 6, 2),
        blocker('meteor', 6, 4, 1),
        blocker('meteor', 6, 5, 1),
        blocker('meteor', 6, 6, 1),
      ],
    ),
    challenge: 'The near-final board asks for control in three time scales: unlock, pair, then contain.',
    difficulty: 5,
  },
  {
    id: 50,
    chapter: 5,
    title: 'Lumenwild Apex',
    subtitle: 'Stitch the five wild lights together and open the living sky.',
    width: 11,
    height: 10,
    moves: 24,
    seed: 50550,
    palette: 'nightloom-violet',
    mechanics: [
      'classic',
      'holes',
      'portals',
      'conveyor',
      'locked',
      'layers',
      'hazard',
      'multiEntry',
      'spawnLimits',
    ],
    objectives: [
      {
        id: 'apex-score',
        kind: 'score',
        target: 26000,
        label: 'Reach 26,000 glow',
      },
      {
        id: 'apex-drop',
        kind: 'drop',
        target: 6,
        pieceType: 3,
        label: 'Drop 6 Prism Pears into the apex gates',
      },
      {
        id: 'apex-combo',
        kind: 'combo',
        target: 3,
        label: 'Activate 3 special pairings',
      },
    ],
    cells: constrainedBoard(
      11,
      10,
      [[0, 1], [0, 3], [0, 5], [0, 7], [0, 9], [1, 0], [4, 0], [8, 10], [9, 8]],
      [
        ...holes([2, 0], [2, 10], [3, 1], [3, 9], [6, 1], [6, 9], [7, 0], [7, 10]),
        ...spawns([0, 1], [0, 3], [0, 5], [0, 7], [0, 9], [1, 0], [4, 0], [8, 10], [9, 8]),
        ...exits([9, 2], [9, 4], [9, 5], [9, 6], [9, 8]),
        ...portals('apex-a', [1, 2], [8, 8]),
        ...portals('apex-b', [1, 8], [8, 2]),
        ...conveyors('right', [3, 2], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7], [3, 8]),
        ...conveyors('left', [6, 2], [6, 3], [6, 4], [6, 5], [6, 6], [6, 7], [6, 8]),
        ...hazards(1, [4, 3], [4, 4], [4, 5], [4, 6], [4, 7], [5, 3], [5, 4], [5, 5], [5, 6], [5, 7]),
        ...hazards(2, [5, 5]),
        lock(2, 2),
        lock(2, 8),
        lock(7, 2),
        lock(7, 8),
        blocker('meteor', 2, 4, 3),
        blocker('meteor', 2, 5, 3),
        blocker('meteor', 2, 6, 3),
        blocker('bramble', 7, 4, 2),
        blocker('bramble', 7, 5, 2),
        blocker('bramble', 7, 6, 2),
        blocker('glass', 8, 4, 2),
        blocker('glass', 8, 5, 2),
        blocker('glass', 8, 6, 2),
      ],
    ),
    challenge: 'Campaign apex: choose which routes to open, use three intentional pairings, and keep the center alive long enough for six drops.',
    difficulty: 5,
  },
];

export const getLevel = (id: number): LevelSpec => LEVELS[Math.max(0, Math.min(LEVELS.length - 1, Math.floor(id) - 1))];

export const getChapter = (id: number): ChapterMeta => CHAPTERS[Math.max(0, Math.min(CHAPTERS.length - 1, Math.floor(id) - 1))];
